// Ficha de empresa: si es revendedor de 3CX, sus datos para los avisos (idioma, correo, contacto) y sus licencias por
// vencer. No aparece en empresas que no son revendedores.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { avisarPartner, type DatosLicenciasEmpresa, datosLicenciasEmpresa, guardarDatosLicenciasEmpresa, licencias3cx } from '../api/licencias3cx';
import { shortDate } from '../lib/format';
import { Icon } from '../lib/icons';
import { diasHasta, tonoDias } from '../lib/licencias3cx';
import { Pill } from './bits';
import { toast } from './toasts';

export function LicenciasEmpresa({ empresaId }: { empresaId: string }) {
  const qc = useQueryClient();
  const d = useQuery({ queryKey: ['licencias-empresa', 'datos', empresaId], queryFn: () => datosLicenciasEmpresa(empresaId) });
  const ls = useQuery({ queryKey: ['licencias-empresa', 'lista', empresaId], queryFn: () => licencias3cx(empresaId) });
  const [v, setV] = useState<DatosLicenciasEmpresa | null>(null);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => {
    if (d.data) setV(d.data);
  }, [d.data]);
  const activas = (ls.data ?? []).filter((l) => l.estado === 'POR_VENCER' || l.estado === 'VENCIDA');
  if (!d.data || !v || (!d.data.partner3cxId && !activas.length)) return null;
  const cambiado = v.idioma !== d.data.idioma || v.correo !== d.data.correo || v.contacto !== d.data.contacto;

  const guardar = async () => {
    setGuardando(true);
    try {
      await guardarDatosLicenciasEmpresa(empresaId, v);
      await qc.invalidateQueries({ queryKey: ['licencias-empresa'] });
      void qc.invalidateQueries({ queryKey: ['licencias3cx'] });
      toast('Datos de avisos guardados.');
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setGuardando(false);
    }
  };
  const avisar = async () => {
    try {
      const x = await avisarPartner(empresaId);
      const e = x.envios[0];
      toast(e?.enviado ? `Aviso enviado a ${e.correo}.` : (e?.error ?? x.mensaje ?? 'Nada que avisar.'), e && !e.enviado ? 'error' : undefined);
      void qc.invalidateQueries({ queryKey: ['licencias-empresa'] });
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  };

  return (
    <section className="box">
      <div className="box-h">
        <h2 className="lbl">Licencias 3CX{d.data.partner3cxId ? ` · partner ${d.data.partner3cxId}` : ''}</h2>
        {activas.length && v.correo ? (
          <button className="btn sm ghost" onClick={() => void avisar()}>
            <Icon name="send" /> Enviar aviso ahora
          </button>
        ) : null}
      </div>
      <div className="lic-datos">
        <label>
          Idioma
          <select id="lic-idioma" value={v.idioma ?? ''} onChange={(e) => setV({ ...v, idioma: (e.target.value || null) as DatosLicenciasEmpresa['idioma'] })}>
            <option value="">—</option>
            <option value="ES">Español</option>
            <option value="EN">Inglés</option>
          </select>
        </label>
        <label>
          Correo para avisos
          <input id="lic-correo" value={v.correo} onChange={(e) => setV({ ...v, correo: e.target.value })} placeholder="licencias@partner.com" />
        </label>
        <label>
          Contacto
          <input id="lic-contacto" value={v.contacto} onChange={(e) => setV({ ...v, contacto: e.target.value })} />
        </label>
        {cambiado ? (
          <button className="btn sm primary" onClick={() => void guardar()} disabled={guardando}>
            Guardar
          </button>
        ) : null}
      </div>
      {activas.length ? (
        <div className="rows">
          {activas.map((l) => {
            const dias = diasHasta(l.vence);
            return (
              <div key={l.id} className="row" style={{ gridTemplateColumns: 'minmax(0,1fr) auto', padding: '7px 2px' }}>
                <span>
                  <span className="mono">{l.clave}</span> · {l.edicion}
                  {l.llamadas ? ` ${l.llamadas}SC` : ''} · vence {l.vence ? shortDate(l.vence) : '—'}
                  {l.aviso45 || l.aviso25 ? <span className="note"> · avisado {l.aviso25 ? '25' : '45'} d</span> : null}
                </span>
                {dias == null ? null : <Pill tone={tonoDias(dias)}>{dias} d</Pill>}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="note">Sin licencias por vencer en los próximos 90 días.</p>
      )}
    </section>
  );
}
